export type PhoneDialOption = {
    dial: string;
};

/** Same rule as Laravel: /^(\+[1-9][0-9]{1,14}|[0-9]{10})$/ with min length 10 and max length 16. */
export const PHONE_VALIDATION_PATTERN = /^(\+[1-9][0-9]{1,14}|[0-9]{10})$/;

export const PHONE_MIN_LENGTH = 10;
export const PHONE_MAX_LENGTH = 16;

export function isValidPhone(value: unknown): boolean {
    const normalized = String(value ?? "").trim();

    if (!normalized || normalized.length < PHONE_MIN_LENGTH || normalized.length > PHONE_MAX_LENGTH) {
        return false;
    }

    return PHONE_VALIDATION_PATTERN.test(normalized);
}

export const PHONE_INVALID_MESSAGE_ID = "authentication.register.error.phone.invalid";

export function getPhoneValidationError(
    value: unknown,
    formatMessage: (id: string) => string,
): string | undefined {
    const normalized = String(value ?? "").trim();

    if (!normalized) {
        return undefined;
    }

    if (!isValidPhone(normalized)) {
        return formatMessage(PHONE_INVALID_MESSAGE_ID);
    }

    return undefined;
}

export function stripToDigits(raw: unknown): string {
    return String(raw ?? "")
        .trim()
        .replace(/\D/g, "");
}

function stripToDialDigits(raw: unknown): string {
    return String(raw ?? "")
        .trim()
        .replace(/[^\d+]/g, "");
}

/** Drop trunk prefix (e.g. 06… → 6…) before appending to a dial code. */
export function normalizeNationalDigits(raw: unknown): string {
    const digits = stripToDigits(raw);
    return digits.startsWith("0") ? digits.slice(1) : digits;
}

export function buildE164Phone(
    dial: string,
    nationalRaw: unknown,
    whenEmpty: string = "",
): string {
    const national = normalizeNationalDigits(nationalRaw);
    return national ? `${dial}${national}` : whenEmpty;
}

export function splitE164Phone(
    valueRaw: unknown,
    options: PhoneDialOption[],
    fallbackDial: string,
): { dial: string; national: string } {
    const value = stripToDialDigits(valueRaw);
    if (!value.startsWith("+")) {
        return { dial: fallbackDial, national: normalizeNationalDigits(value) };
    }

    const digits = stripToDigits(value);
    const optionsByLongestDial = [...options].sort((a, b) => b.dial.length - a.dial.length);

    for (const opt of optionsByLongestDial) {
        const dialDigits = stripToDigits(opt.dial);
        if (digits.startsWith(dialDigits)) {
            return {
                dial: opt.dial,
                national: normalizeNationalDigits(digits.slice(dialDigits.length)),
            };
        }
    }

    const dialDigits = digits.slice(0, Math.min(3, digits.length));
    return {
        dial: `+${dialDigits || stripToDigits(fallbackDial) || "33"}`,
        national: normalizeNationalDigits(digits.slice(dialDigits.length)),
    };
}
